import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, CircularProgress, Alert, Button } from '@mui/material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import './exportDataPage.css';

const getMonthDays = (year, month) => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const days = [];

  for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
    days.push({
      date: new Date(d),
      dayName: new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(new Date(d)) // Jour de la semaine
    });
  }

  return days;
};

const getMonthNameAndYear = (date) => {
  const options = { month: 'long', year: 'numeric' };
  return new Intl.DateTimeFormat('fr-FR', options).format(date);
};

const ExportDataPage = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysOfMonth, setDaysOfMonth] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dayPresenceTotals, setDayPresenceTotals] = useState({});


  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);

        // Récupérer les jours disponibles
        const availableDaysRef = doc(db, 'settings', 'selectableDays');
        const availableDaysSnap = await getDoc(availableDaysRef);
        const availableDaysData = availableDaysSnap.exists() ? availableDaysSnap.data().days : {};

        // Récupérer tous les utilisateurs
        const usersRef = collection(db, 'users');
        const usersSnap = await getDocs(usersRef);

        const currentMonthDays = getMonthDays(currentMonth.getFullYear(), currentMonth.getMonth())
          .filter(({ date }) => availableDaysData[date.toDateString()]); // Filtrer les jours non disponibles

        const dayPresenceTotalsInit = currentMonthDays.reduce((acc, { date }) => {
          acc[date.toDateString()] = 0;
          return acc;
        }, {});

        setDaysOfMonth(currentMonthDays);
        setDayPresenceTotals(dayPresenceTotalsInit);

        // Préparer une liste de promesses pour récupérer les enfants en parallèle
        const childrenPromises = usersSnap.docs
          .filter(userDoc => {
            const userData = userDoc.data();
            // Filtrer les utilisateurs archivés
            return !userData.isArchived;
          })
          .map(async (userDoc) => {
            const childrenRef = collection(db, 'users', userDoc.id, 'children');
            const childrenSnap = await getDocs(childrenRef);

          const childPromises = childrenSnap.docs.map(async (childDoc) => {
            const attendanceSnap = await getDoc(doc(db, 'users', userDoc.id, 'children', childDoc.id));
            const attendanceData = attendanceSnap.exists() ? attendanceSnap.data().attendance || [] : [];

            const attendanceMap = currentMonthDays.reduce((acc, { date }) => {
              const dateString = date.toDateString();
              const isPresent = attendanceData.some(dateStr => new Date(dateStr).toDateString() === dateString);
              if (isPresent) dayPresenceTotalsInit[dateString] += 1;
              acc[dateString] = isPresent;
              return acc;
            }, {});

            return {
              childName: childDoc.data().name || '',
              childFirstName: childDoc.data().firstName || '',
              childClass: childDoc.data().class || '',
              attendance: attendanceMap,
              totalDaysPresent: Object.values(attendanceMap).filter(present => present).length
            };
          });

          // Résoudre toutes les promesses des enfants
          return Promise.all(childPromises);
        });

        // Résoudre toutes les promesses des enfants pour tous les utilisateurs
        const childrenResults = (await Promise.all(childrenPromises)).flat();

        // Trier les données par ordre alphabétique des noms des enfants
        childrenResults.sort((a, b) => {
          const nameA = `${a.childName} ${a.childFirstName}`.toUpperCase();
          const nameB = `${b.childName} ${b.childFirstName}`.toUpperCase();
          return nameA.localeCompare(nameB);
        });

        setAttendanceData(childrenResults);
        setDayPresenceTotals(dayPresenceTotalsInit);
      } catch (err) {
        setError('Erreur lors de la récupération des données de présence.');
        console.error('Erreur lors de la récupération des données de présence :', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAttendanceData();
  }, [currentMonth]);

  const handleMonthChange = (delta) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + delta);
      return newMonth;
    });
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet([

      ...attendanceData.map(row => ({
        'Nom de l\'Enfant': `${row.childName} ${row.childFirstName}`,
        'Classe': row.childClass, // Nouvelle donnée
        ...daysOfMonth.reduce((acc, { date }) => ({
          ...acc,
          [`${new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date).slice(0,2)}.${date.getDate()}`]: row.attendance[date.toDateString()] ? 1 : 0
        }), {}),
        'Total': row.totalDaysPresent
      })),
      {
        "Nom de l'Enfant": 'Total',
        "Classe": '', // Cellule vide pour la colonne Classe
        ...daysOfMonth.reduce((acc, { date }) => ({
          ...acc,
          [`${new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date).slice(0,2)}.${date.getDate()}`]: dayPresenceTotals[date.toDateString()] || 0
        }), {}),
        "Total": Object.values(dayPresenceTotals).reduce((acc, count) => acc + count, 0)
      }
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Présences');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `Presence_${getMonthNameAndYear(currentMonth)}.xlsx`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    // Validation pour éviter l'accès aux propriétés undefined
    const isValidDayPresenceTotals = Object.keys(dayPresenceTotals).length > 0;
    const isValidAttendanceData = attendanceData.length > 0;

    return (
      <TableContainer className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="fixed-header-cell">Nom de l'Enfant</TableCell>
              <TableCell>Classe</TableCell>
              {daysOfMonth.map(({ date, dayName }, index) => (
                <TableCell key={`${index}-${date.toDateString()}`}>
                  {`${dayName} ${date.getDate()}`}
                </TableCell>
              ))}
              <TableCell style={{ fontWeight: 'bold' }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isValidAttendanceData ? (
              attendanceData.map((row, index) => (
                <TableRow key={`${row.childName}-${row.childFirstName}-${index}`}>
                  <TableCell className="fixed-col">{row.childName} {row.childFirstName}</TableCell>
                  <TableCell>{row.childClass}</TableCell>
                  {daysOfMonth.map(({ date }) => (
                    <TableCell
                      key={`${row.childName}-${row.childFirstName}-${date.toDateString()}`}
                      style={{
                        backgroundColor: row.attendance[date.toDateString()] ? '#ffcccc' : 'transparent',
                      }}
                    >
                      {row.attendance[date.toDateString()] ? '1' : '0'}
                    </TableCell>
                  ))}
                  <TableCell style={{ fontWeight: 'bold' }}>{row.totalDaysPresent}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={daysOfMonth.length + 2} align="center">Aucune donnée disponible</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className="fixed-col" style={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell></TableCell>
              {isValidDayPresenceTotals ? (
                daysOfMonth.map(({ date }) => (
                  <TableCell key={`${date.toDateString()}`} style={{ fontWeight: 'bold' }}>
                    {dayPresenceTotals[date.toDateString()] || 0}
                  </TableCell>
                ))
              ) : (
                <TableCell colSpan={daysOfMonth.length} align="center">Aucun total disponible</TableCell>
              )}
              <TableCell style={{ fontWeight: 'bold' }}>
                {Object.values(dayPresenceTotals).reduce((acc, count) => acc + count, 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };


  return (
    <div>
      <Paper sx={{ padding: 2, marginTop: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ marginRight: 2, fontWeight: 'bold' }}>
          Tableau de Présence ({attendanceData.length} enfants)
        </Typography>

        <Box mb={2} display="flex" alignItems="center">
          <Button variant="text" color="primary" onClick={() => handleMonthChange(-1)}>
            <ArrowBackIosIcon />
          </Button>
          <Box flex={1} textAlign="center">
            <Typography variant="h6">
              {getMonthNameAndYear(currentMonth).toLocaleUpperCase()}
            </Typography>
          </Box>
          <Button variant="text" color="primary" onClick={() => handleMonthChange(1)}>
            <ArrowForwardIosIcon />
          </Button>
        </Box>
        {!loading && (
          <Box mb={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" color="secondary" onClick={exportToExcel} startIcon={<FileDownloadIcon />}>
              Exporter
            </Button>
          </Box>
        )}
        {renderContent()}
      </Paper>
    </div>
  );
};

export default ExportDataPage;